import { useCallback, useEffect, useMemo, useState } from 'react'
import { authFetch } from '@/auth/authFetch'
import { API_BASE_URL } from '@/analysis/endpoints'
import { useAuth } from '@/auth/hooks'
import { useCatalogStore, type CatalogStreamItem } from '@/store/catalogStore'

type Mode =
  | { kind: 'idle' }
  | { kind: 'create' }
  | { kind: 'edit'; row: CatalogStreamItem }
  | { kind: 'delete'; row: CatalogStreamItem }

type FormState = {
  stream_id: string
  type: string
  target_device: string
  stream_url: string
  studio_code: string
  game_code: string
  brand_ids: string
}

const emptyForm = (): FormState => ({
  stream_id: '',
  type: 'flv',
  target_device: 'pc',
  stream_url: '',
  studio_code: '',
  game_code: '',
  brand_ids: '',
})

function toForm(row: CatalogStreamItem): FormState {
  return {
    stream_id: row.stream_id,
    type: row.type || 'flv',
    target_device: row.target_device || 'pc',
    stream_url: row.stream_url || '',
    studio_code: row.studio_code || '',
    game_code: row.game_code || '',
    brand_ids: (row.brand_ids ?? []).join(', '),
  }
}

export default function StreamsView() {
  const { user } = useAuth()
  const perms = user?.permissions ?? []
  const canWrite = perms.includes('stream:write') || perms.includes('*')
  const canDelete = perms.includes('stream:delete') || perms.includes('*')
  const canReadUrl = perms.includes('stream:read') || perms.includes('stream:write') || perms.includes('*')

  const setStreams = useCatalogStore((s) => s.setStreams)
  const [rows, setRows] = useState<CatalogStreamItem[]>([])
  const [includeDeleted, setIncludeDeleted] = useState(false)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [mode, setMode] = useState<Mode>({ kind: 'idle' })
  const [form, setForm] = useState<FormState>(emptyForm)
  const [busy, setBusy] = useState(false)
  const [q, setQ] = useState('')

  const refresh = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const qs = includeDeleted ? '?includeDeleted=1' : ''
      const res = await authFetch(`${API_BASE_URL}/api/catalog/streams${qs}`)
      if (!res.ok) {
        setError(res.status === 403
          ? 'You need stream:read to manage the stream catalog.'
          : `Failed to load streams (${res.status}).`)
        return
      }
      const body = await res.json() as { streams?: CatalogStreamItem[] }
      const list = body.streams ?? []
      setRows(list)
      setStreams(list.filter((s) => !s.deleted_at))
    } catch (e) {
      setError(String((e as Error).message ?? e))
    } finally {
      setLoading(false)
    }
  }, [includeDeleted, setStreams])

  useEffect(() => { void refresh() }, [refresh])

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase()
    if (!needle) return rows
    return rows.filter((r) =>
      [r.stream_id, r.type, r.studio_code, r.game_code, r.stream_url, r.url_masked]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(needle)),
    )
  }, [rows, q])

  async function submitForm() {
    setBusy(true)
    setError(null)
    try {
      const payload = {
        stream_id: form.stream_id.trim(),
        type: form.type.trim(),
        target_device: form.target_device.trim(),
        stream_url: form.stream_url.trim(),
        studio_code: form.studio_code.trim(),
        game_code: form.game_code.trim(),
        brand_ids: form.brand_ids.split(',').map((s) => s.trim()).filter(Boolean),
      }
      if (mode.kind === 'create') {
        const res = await authFetch(`${API_BASE_URL}/api/catalog/streams`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        })
        if (!res.ok) throw new Error(`Create failed (${res.status})`)
      } else if (mode.kind === 'edit') {
        const res = await authFetch(
          `${API_BASE_URL}/api/catalog/streams/${encodeURIComponent(mode.row.stream_id)}`,
          {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              type: payload.type,
              target_device: payload.target_device,
              stream_url: payload.stream_url || undefined,
              studio_code: payload.studio_code,
              game_code: payload.game_code,
              brand_ids: payload.brand_ids,
            }),
          },
        )
        if (!res.ok) throw new Error(`Update failed (${res.status})`)
      }
      setMode({ kind: 'idle' })
      setForm(emptyForm())
      await refresh()
    } catch (e) {
      setError(String((e as Error).message ?? e))
    } finally {
      setBusy(false)
    }
  }

  async function confirmDelete() {
    if (mode.kind !== 'delete') return
    setBusy(true)
    try {
      const res = await authFetch(
        `${API_BASE_URL}/api/catalog/streams/${encodeURIComponent(mode.row.stream_id)}`,
        { method: 'DELETE' },
      )
      if (!res.ok) throw new Error(`Delete failed (${res.status})`)
      setMode({ kind: 'idle' })
      await refresh()
    } catch (e) {
      setError(String((e as Error).message ?? e))
    } finally {
      setBusy(false)
    }
  }

  async function exportPlaylist() {
    setBusy(true)
    try {
      const res = await authFetch(`${API_BASE_URL}/api/catalog/export/playlist`)
      if (!res.ok) throw new Error(`Export failed (${res.status})`)
      const body = await res.json()
      const blob = new Blob([JSON.stringify(body.streams ?? [], null, 2)], { type: 'application/json' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = 'streams-playlist.json'
      a.click()
      URL.revokeObjectURL(url)
    } catch (e) {
      setError(String((e as Error).message ?? e))
    } finally {
      setBusy(false)
    }
  }

  const editing = mode.kind === 'create' || mode.kind === 'edit'

  return (
    <div className="panel" style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <header style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', borderBottom: '0.5px solid var(--bd-0)' }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--t-0)' }}>Streams</div>
          <div style={{ fontSize: 11, color: 'var(--t-2)' }}>
            Catalog source of truth for inventory type, studio, game, and playable URL.
          </div>
        </div>
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search…"
          style={inputStyle}
        />
        <label style={{ fontSize: 11, color: 'var(--t-2)', display: 'flex', alignItems: 'center', gap: 6 }}>
          <input type="checkbox" checked={includeDeleted} onChange={(e) => setIncludeDeleted(e.target.checked)} />
          Show deleted
        </label>
        <button onClick={() => void refresh()} style={btnGhost} disabled={busy}>Refresh</button>
        <button onClick={() => void exportPlaylist()} style={btnGhost} disabled={busy || !canReadUrl}>Export playlist</button>
        {canWrite && (
          <button
            onClick={() => { setForm(emptyForm()); setMode({ kind: 'create' }) }}
            style={btnPrimary}
          >
            + New stream
          </button>
        )}
      </header>

      {loading && <div style={{ padding: 16, fontSize: 12, color: 'var(--t-2)' }}>Loading…</div>}
      {error && <div style={{ padding: 16, fontSize: 12, color: 'var(--crit)' }}>{error}</div>}

      {!loading && !error && (
        <div style={{ flex: 1, overflow: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead style={{ position: 'sticky', top: 0, background: 'var(--bg-1)' }}>
              <tr>
                {['Stream', 'Type', 'Device', 'Studio', 'Game', 'Brands', 'URL', ''].map((h) => (
                  <th key={h} style={thStyle}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((row) => (
                <tr key={row.stream_id} style={{ opacity: row.deleted_at ? 0.55 : 1 }}>
                  <td style={tdStyle}>
                    <div style={{ fontWeight: 600 }}>{row.stream_id}</div>
                    {row.deleted_at && <div style={{ fontSize: 10, color: 'var(--t-2)' }}>deleted</div>}
                  </td>
                  <td style={tdStyle}>{row.type || '—'}</td>
                  <td style={tdStyle}>{row.target_device || '—'}</td>
                  <td style={tdStyle}>{row.studio_code || '—'}</td>
                  <td style={tdStyle}>{row.game_code || '—'}</td>
                  <td style={tdStyle}>{(row.brand_ids ?? []).join(', ') || '—'}</td>
                  <td style={{ ...tdStyle, maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {canReadUrl ? (row.stream_url || row.url_masked || '—') : (row.url_masked || '—')}
                  </td>
                  <td style={tdStyle}>
                    <div style={{ display: 'flex', gap: 6 }}>
                      {canWrite && (
                        <button
                          style={btnGhost}
                          onClick={() => { setForm(toForm(row)); setMode({ kind: 'edit', row }) }}
                        >
                          Edit
                        </button>
                      )}
                      {canDelete && !row.deleted_at && (
                        <button style={btnGhost} onClick={() => setMode({ kind: 'delete', row })}>Delete</button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={8} style={{ ...tdStyle, color: 'var(--t-2)' }}>No streams</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <div style={modalBackdrop}>
          <div style={modalCard}>
            <div style={{ fontWeight: 600, marginBottom: 10 }}>
              {mode.kind === 'create' ? 'Create stream' : `Edit ${mode.kind === 'edit' ? mode.row.stream_id : ''}`}
            </div>
            <div style={{ display: 'grid', gap: 8 }}>
              {mode.kind === 'create' && (
                <Field label="Stream ID" value={form.stream_id} onChange={(v) => setForm({ ...form, stream_id: v })} />
              )}
              <Field label="Type" value={form.type} onChange={(v) => setForm({ ...form, type: v })} placeholder="flv | millicast" />
              <Field label="Target device" value={form.target_device} onChange={(v) => setForm({ ...form, target_device: v })} placeholder="pc | mb" />
              <Field
                label={mode.kind === 'edit' ? 'Stream URL (leave blank to keep)' : 'Stream URL'}
                value={form.stream_url}
                onChange={(v) => setForm({ ...form, stream_url: v })}
              />
              <Field label="Studio code" value={form.studio_code} onChange={(v) => setForm({ ...form, studio_code: v })} />
              <Field label="Game code" value={form.game_code} onChange={(v) => setForm({ ...form, game_code: v })} />
              <Field label="Brand IDs (comma-separated)" value={form.brand_ids} onChange={(v) => setForm({ ...form, brand_ids: v })} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 14 }}>
              <button style={btnGhost} onClick={() => setMode({ kind: 'idle' })} disabled={busy}>Cancel</button>
              <button style={btnPrimary} onClick={() => void submitForm()} disabled={busy}>Save</button>
            </div>
          </div>
        </div>
      )}

      {mode.kind === 'delete' && (
        <div style={modalBackdrop}>
          <div style={modalCard}>
            <div style={{ fontWeight: 600, marginBottom: 8 }}>Delete stream?</div>
            <div style={{ fontSize: 12, color: 'var(--t-2)', marginBottom: 14 }}>
              Soft-delete <code>{mode.row.stream_id}</code>. Snapshots remain; inventory joins will hide it.
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button style={btnGhost} onClick={() => setMode({ kind: 'idle' })} disabled={busy}>Cancel</button>
              <button style={{ ...btnPrimary, background: 'var(--crit)' }} onClick={() => void confirmDelete()} disabled={busy}>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function Field(props: {
  label: string
  value: string
  onChange: (v: string) => void
  placeholder?: string
}) {
  return (
    <label style={{ display: 'grid', gap: 4, fontSize: 11, color: 'var(--t-2)' }}>
      {props.label}
      <input
        value={props.value}
        placeholder={props.placeholder}
        onChange={(e) => props.onChange(e.target.value)}
        style={{ ...inputStyle, width: '100%' }}
      />
    </label>
  )
}

const thStyle: React.CSSProperties = {
  textAlign: 'left',
  padding: '8px 12px',
  borderBottom: '0.5px solid var(--bd-0)',
  color: 'var(--t-2)',
  fontWeight: 500,
  fontSize: 11,
}
const tdStyle: React.CSSProperties = {
  padding: '8px 12px',
  borderBottom: '0.5px solid var(--bd-0)',
  color: 'var(--t-1)',
  verticalAlign: 'top',
}
const inputStyle: React.CSSProperties = {
  fontSize: 12,
  padding: '6px 8px',
  borderRadius: 6,
  border: '0.5px solid var(--bd-0)',
  background: 'var(--bg-0)',
  color: 'var(--t-0)',
}
const btnGhost: React.CSSProperties = {
  fontSize: 11,
  padding: '6px 10px',
  borderRadius: 6,
  border: '0.5px solid var(--bd-0)',
  background: 'transparent',
  color: 'var(--t-1)',
  cursor: 'pointer',
}
const btnPrimary: React.CSSProperties = {
  ...btnGhost,
  background: 'var(--info-bg)',
  color: 'var(--info)',
  borderColor: 'var(--info-bd)',
  fontWeight: 600,
}
const modalBackdrop: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.45)',
  display: 'grid',
  placeItems: 'center',
  zIndex: 80,
}
const modalCard: React.CSSProperties = {
  width: 440,
  maxWidth: '92vw',
  background: 'var(--bg-1)',
  border: '0.5px solid var(--bd-0)',
  borderRadius: 10,
  padding: 16,
}
